import Auth from "@creditsafe/upp-authentication";
import { accessToken } from "../../shared/constants/localstorage.constant";
import { getAccessTokenFromStorage } from "./access-token";
import { store } from "../../store/store";
import { userSignedIn, userSignedOut } from "../../store/UserSlice";
import {
  clearAuthSession,
  isAuthSessionBeingCleared,
  syncActiveTeamWithAuthToken,
} from "./clear-auth-session";

export { clearAuthSession, syncActiveTeamWithAuthToken };

let isAuthSessionCleanupInstalled = false;
let isEmbeddedInUpp = false;

const resolveIsUppContext = (): boolean =>
  isEmbeddedInUpp || store.getState().user.isUpp;

export const clearSessionAfterLogout = (): void => {
  store.dispatch(userSignedOut());
};

export const syncUserAuthStateWithToken = (): void => {
  const token = getAccessTokenFromStorage();
  const { isSignedIn } = store.getState().user;
  const isUpp = resolveIsUppContext();

  if (token && !isSignedIn) {
    if (!isUpp) {
      store.dispatch(userSignedIn(token));
    }
    return;
  }

  if (!token) {
    if (isSignedIn) {
      store.dispatch(userSignedOut());
    } else {
      syncActiveTeamWithAuthToken();
    }
  }
};

export const installAuthSessionCleanup = (isUpp?: boolean): void => {
  if (isUpp !== undefined) {
    isEmbeddedInUpp = isUpp;
  }

  syncUserAuthStateWithToken();

  if (isAuthSessionCleanupInstalled) {
    return;
  }
  isAuthSessionCleanupInstalled = true;

  const originalRemoveItem = Storage.prototype.removeItem;
  Storage.prototype.removeItem = function removeItemWithAuthCleanup(
    this: Storage,
    key: string
  ): void {
    const hadAccessToken =
      key === accessToken && this.getItem(accessToken) !== null;

    originalRemoveItem.call(this, key);

    if (
      key === accessToken &&
      hadAccessToken &&
      !isAuthSessionBeingCleared()
    ) {
      syncUserAuthStateWithToken();
    }
  };

  const handleAccessTokenStorageChange = (event: StorageEvent): void => {
    if (event.key === accessToken) {
      syncUserAuthStateWithToken();
    }
  };

  window.addEventListener("storage", handleAccessTokenStorageChange);
  window.addEventListener("focus", syncUserAuthStateWithToken);
  document.addEventListener("visibilitychange", syncUserAuthStateWithToken);
};

export const registerOidcLogoutHandlers = (): void => {
  const userManager = Auth._getUserManager?.();
  userManager?.events.addUserSignedOut(clearSessionAfterLogout);
};

export const wrapAuthSignOut = (): void => {
  if (typeof Auth.signOut !== "function") {
    return;
  }

  const originalSignOut = Auth.signOut.bind(Auth);

  Auth.signOut = (): void => {
    clearSessionAfterLogout();
    originalSignOut();
  };
};

export const validateOidcSession = async (): Promise<void> => {
  if (!getAccessTokenFromStorage()) {
    syncUserAuthStateWithToken();
    return;
  }

  const userManager = Auth._getUserManager?.();
  if (!userManager) {
    return;
  }

  try {
    const user = await userManager.getUser();
    if (user?.expired) {
      clearSessionAfterLogout();
    }
  } catch {
    // Avoid clearing the session when OIDC is still initialising.
  }
};

export const initStandaloneAuthSessionCleanup = async (): Promise<void> => {
  installAuthSessionCleanup();
  registerOidcLogoutHandlers();
  wrapAuthSignOut();
  await validateOidcSession();
};
